import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, Button, TextField, Grid, Dialog, DialogTitle, DialogContent, DialogActions, List, ListItem, ListItemText, IconButton, Pagination, InputAdornment } from '@mui/material';
import RichTextEditor from './RichTextEditor/RichTextEditor';
import { TEMPLATE_VARIABLE_GROUPS } from '../constants/templateVariables';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import EditIcon from '@mui/icons-material/Edit';
import InfoIcon from '@mui/icons-material/Info';
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
import { uploadFileToGCS } from '../utils/api'; // Adjust the import path as needed
import { useTemplates } from '../utils/useTemplates';
import { validateTemplateName } from '../utils/sanitizeTemplateName';
import SavedPopup from './SavedPopup';

const TemplateEditor = () => {
  const {
    templates,
    editingTemplate,
    isLoading,
    error,
    handleEditTemplate,
    handleUpdateTemplate,
    handleCreateTemplate,
    handleDeleteTemplate,
  } = useTemplates();

  const [openDialog, setOpenDialog] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templateNameError, setTemplateNameError] = useState(null);
  const [subject, setSubject] = useState('');
  const [content, setContent] = useState('');
  const [pdfAttachment, setPdfAttachment] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const templatesPerPage = 10;

  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  const [showSavedPopup, setShowSavedPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState(null);
  const [popupIsError, setPopupIsError] = useState(false);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);

  useEffect(() => {
    if (editingTemplate) {
      setTemplateName(editingTemplate.name);
      setSubject(editingTemplate.subject || '');
      setContent(editingTemplate.content || '');
      setPdfAttachment(editingTemplate.pdfAttachment || null);
      setTemplateNameError(null);
      setOpenDialog(true);
    }
  }, [editingTemplate]);

  const handleTemplateNameChange = (e) => {
    const value = e.target.value;
    setTemplateName(value);
    
    // Validate on change
    const validation = validateTemplateName(value);
    if (validation.isValid) {
      setTemplateNameError(null);
    } else {
      setTemplateNameError(validation.error);
    }
  };

  const handleSubjectChange = (e) => {
    setSubject(e.target.value);
  };

  const handleContentChange = (content) => {
    setContent(content);
  };

  const handlePdfUpload = async (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setIsUploading(true);
      setUploadError(null);
      try {
        const { publicUrl } = await uploadFileToGCS(file);
        setPdfAttachment({ name: file.name, url: publicUrl });
      } catch (error) {
        console.error('Error uploading PDF:', error);
        setUploadError(
          error instanceof Error ? error.message : 'Unknown error occurred'
        );
      } finally {
        setIsUploading(false);
      }
    }
  };

  const handleSave = async () => {
    if (!templateName || !subject || !content) {
      // Add error handling for required fields
      alert("Template name, subject, and content are required.");
      return;
    }

    // Validate the template name
    const validation = validateTemplateName(templateName);
    if (!validation.isValid) {
      setTemplateNameError(validation.error);
      return;
    }

    const templateData = {
      name: templateName.trim(),
      subject,
      content,
      pdfAttachment: pdfAttachment ? {
        name: pdfAttachment.name || '',
        url: pdfAttachment.url || ''
      } : null
    };

    let result;
    if (editingTemplate) {
      result = await handleUpdateTemplate({ ...editingTemplate, ...templateData });
    } else {
      result = await handleCreateTemplate(templateData);
    }

    if (result.success) {
      resetForm();
      setOpenDialog(false);
      setPopupMessage(null);
      setPopupIsError(false);
      setShowSavedPopup(true);
    } else {
      // Show error notification
      setPopupMessage(result.error);
      setPopupIsError(true);
      setShowSavedPopup(true);
      // Keep dialog open so user can fix the error
    }
  };

  const handleCloseSavedPopup = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setShowSavedPopup(false);
    setPopupMessage(null);
    setPopupIsError(false);
  };

  const resetForm = () => {
    setTemplateName('');
    setTemplateNameError(null);
    setSubject('');
    setContent('');
    setPdfAttachment(null);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const filteredTemplates = templates.filter(template =>
    template.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const indexOfLastTemplate = currentPage * templatesPerPage;
  const indexOfFirstTemplate = indexOfLastTemplate - templatesPerPage;
  const currentTemplates = filteredTemplates.slice(indexOfFirstTemplate, indexOfLastTemplate);

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const handleRemovePdf = () => {
    setPdfAttachment(null);
  };
  const handleCancelDelete = () => {
    setConfirmOpen(false);
    setPendingDeleteId(null);
  };

  const handleConfirmDelete = () => {
    if (pendingDeleteId) {
      handleDeleteTemplate(pendingDeleteId);
    }
    setConfirmOpen(false);
    setPendingDeleteId(null);
  };


  return (
    <div style={{ maxWidth: '800px', margin: '0 auto', padding: '24px' }}>
      <Paper elevation={0} sx={{ p: 2, mb: 3 }}>
        <Grid container alignItems="center" justifyContent="space-between" spacing={2}>
          <Grid item xs>
            <Typography variant="h5">Email Templates</Typography>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => {
                setPopupMessage(null);
                setPopupIsError(false);
                setOpenDialog(true);
              }}
            >
              Add Template
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Paper elevation={3} sx={{ mt: 3, p: 2, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs>
            <Typography variant="h6">Existing Templates</Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search templates..."
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>
        <List sx={{ mt: 2 }}>
          {currentTemplates.map((template) => (
            <ListItem
              key={template._id}
              sx={{
                mb: 1,
                backgroundColor: '#f5f5f5',
                borderRadius: 1,
                '&:hover': {
                  backgroundColor: '#e0e0e0',
                },
              }}
              secondaryAction={
                <>
                  <IconButton edge="end" aria-label="edit" onClick={() => handleEditTemplate(template)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton edge="end" aria-label="delete" onClick={() => { setPendingDeleteId(template._id); setConfirmOpen(true); }}>
                    <DeleteIcon color="error" />
                  </IconButton>
                </>
              }
            >
              <ListItemText
                primary={template.name}
                primaryTypographyProps={{ fontWeight: 'medium' }}
              />
            </ListItem>
          ))}
        </List>
        <Pagination
          count={Math.ceil(filteredTemplates.length / templatesPerPage)}
          page={currentPage}
          onChange={handlePageChange}
          sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}
        />
      </Paper>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingTemplate ? 'Edit Template' : 'Add New Template'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Template Name"
                value={templateName}
                onChange={handleTemplateNameChange}
                sx={{ marginTop: '20px' }}
                required
                error={!templateName || !!templateNameError}
                helperText={templateNameError || (!templateName ? "Template name is required" : "Special characters like /, \\, ?, #, &, =, +, % are not supported")}
              />
            </Grid>

            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2, mb: 2, backgroundColor: '#e3f2fd' }}>
                <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center' }}>
                  <InfoIcon sx={{ mr: 1, color: '#1976d2' }} />
                  Available variables:
                </Typography>
                <Box>
                  {TEMPLATE_VARIABLE_GROUPS.map((group, index) => (
                    <Typography key={group.title} variant="body2" sx={{ mt: index > 0 ? 1 : 0 }}>
                      <Box component="span" sx={{ fontWeight: 600 }}>
                        {group.title}
                      </Box>
                      <Box component="span" sx={{ color: 'text.secondary' }}>
                        {' '}
                        ({group.description}):{' '}
                      </Box>
                      {group.variables.map((v) => v.value).join(', ')}
                    </Typography>
                  ))}
                </Box>
                <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
                  Subject lines commonly use {'{username}'}; only variables listed for a flow are replaced when emails are sent.
                </Typography>
              </Paper>
              <TextField
                fullWidth
                label="Subject"
                value={subject}
                onChange={handleSubjectChange}
                required
                error={!subject}
                helperText={!subject ? 'Subject is required' : 'You can use the same placeholders in the subject (e.g. {username}).'}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Content:
              </Typography>
              <RichTextEditor value={content} onChange={handleContentChange} />
              {!content && (
                <Typography color="error" variant="caption" sx={{ mt: 1 }}>
                  Content is required
                </Typography>
              )}
            </Grid>

            <Grid item xs={12}>
              {pdfAttachment && pdfAttachment.url ? (
                <Paper variant="outlined" sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="body1">{pdfAttachment.name}</Typography>
                  <div>
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={() => window.open(pdfAttachment.url, '_blank')}
                      sx={{ mr: 1 }}
                    >
                      View
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={handleRemovePdf}
                      startIcon={<DeleteIcon />}
                    >
                      Remove
                    </Button>
                  </div>
                </Paper>
              ) : (
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<FileUploadIcon />}
                  fullWidth
                  sx={{ p: 2, border: '2px dashed', borderRadius: 2 }}
                  disabled={isUploading}
                >
                  {isUploading ? 'Uploading...' : 'Upload PDF Attachment'}
                  <input
                    type="file"
                    accept=".pdf"
                    hidden
                    onChange={handlePdfUpload}
                  />
                </Button>
              )}
              {uploadError && (
                <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                  {uploadError}
                </Typography>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setOpenDialog(false);
            resetForm();
          }}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={!templateName || !subject || !content}
          >
            {editingTemplate ? 'Update Template' : 'Save Template'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={confirmOpen} onClose={handleCancelDelete}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this template?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleConfirmDelete}>Delete</Button>
        </DialogActions>
      </Dialog>


      <SavedPopup 
        open={showSavedPopup} 
        onClose={handleCloseSavedPopup}
        isError={popupIsError}
        message={popupMessage}
      />
    </div>
  );
};

export default TemplateEditor;